import { canonicalEmoji } from '@twinden/shared';
import { isRetryable } from './outbox';

interface QueuedReaction {
  kind: 'react';
  conversationId: string;
  messageId: string;
  emoji: string | null;
}

// Setting a reaction (D21, D69, M69 D264), apart from the chat page so the
// order of things can be tested: send, then remember it for the quick row.

/** What tapping `emoji` does: set it, or (the same one again) remove it. */
export function nextReaction(
  mine: string | undefined,
  emoji: string
): string | null {
  const value = canonicalEmoji(emoji);
  return mine !== undefined && canonicalEmoji(mine) === value ? null : value;
}

/**
 * Sends the tap. The reaction is remembered for the quick row only once the
 * server took it, or it was queued to replay offline; a refused one (400)
 * throws and is not remembered, so it can't get stuck in the row.
 */
export async function submitReaction({
  messageId,
  conversationId,
  emoji,
  next,
  send,
  queue,
  remember,
}: {
  messageId: string;
  conversationId: string;
  /** What was tapped (the server toggles on it). */
  emoji: string;
  /** What it sets: the emoji, or null when it removes yours. */
  next: string | null;
  send: (messageId: string, emoji: string) => Promise<unknown>;
  queue: (change: QueuedReaction) => void;
  remember: (emoji: string) => void;
}): Promise<'sent' | 'queued'> {
  try {
    await send(messageId, canonicalEmoji(emoji));
  } catch (err) {
    // Offline: keep it on screen and replay it as a state later (D69).
    if (!isRetryable(err)) throw err;
    queue({ kind: 'react', conversationId, messageId, emoji: next });
    if (next) remember(next);
    return 'queued';
  }
  if (next) remember(next);
  return 'sent';
}
